// API service for VoxiPro AI backend communication
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  timeout: 30000,
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('voxipro_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('voxipro_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API service methods
export const apiService = {
  // Campaign APIs
  campaigns: {
    getAll: (params = {}) => apiClient.get('/campaigns', { params }),
    getDebug: () => apiClient.get('/campaigns-debug'),
    getById: (id) => apiClient.get(`/campaigns/${id}`),
    create: (data) => apiClient.post('/campaigns', data, { timeout: 120000 }),
    update: (id, data) => apiClient.put(`/campaigns/${id}`, data),
    delete: (id) => apiClient.delete(`/campaigns/${id}`),
    start: (id) => apiClient.post(`/campaigns/${id}/start`),
    pause: (id) => apiClient.post(`/campaigns/${id}/pause`),
    export: (id, format = 'xlsx') => apiClient.get(`/campaigns/${id}/export`, { 
      params: { format }, 
      responseType: 'blob',
      timeout: 120000
    }),
    updateCustomFields: (id, data) => apiClient.put(`/campaigns/${id}/custom-fields`, data),
    // Re-roll pre-generated voice audio: clears the cache and regenerates every line
    // with the campaign's CURRENT voice. Required after changing the voice, and the
    // remedy when a cached line contains hallucinated speech.
    regenerateTts: (id) => apiClient.post(`/campaigns/${id}/regenerate-tts`),
    // Contact recycling (feature #5): inspect last-call categories/outcomes, then
    // create a follow-up campaign from the selected buckets.
    recycleOptions: (id) => apiClient.get(`/campaigns/${id}/recycle-options`),
    recycle: (id, data) => apiClient.post(`/campaigns/${id}/recycle`, data, { timeout: 120000 }),
  },

  // Calls APIs
  calls: {
    getAll: (params) => apiClient.get('/calls', { params }),
    manual: (data) => apiClient.post('/calls/manual', data),
    testFlashcall: (data) => apiClient.post('/calls/test-flashcall', data),
    getRecording: (id) => apiClient.get(`/calls/${id}/recording`),
    getTranscript: (id) => apiClient.get(`/calls/${id}/transcript`),
    getEnrichment: (id) => apiClient.get(`/calls/${id}/enrichment`),
    enrich: (id, data) => apiClient.post(`/calls/${id}/enrich`, data),
    updateStatus: (id, data) => apiClient.put(`/calls/${id}/status`, data),
    getConversation: (id) => apiClient.get(`/calls/${id}/conversation`),
    deleteConversation: (id) => apiClient.delete(`/calls/${id}/conversation`),
  },

  // Scripts APIs
  scripts: {
    getAll: (params = {}) => apiClient.get('/scripts', { params }),
    create: (data) => apiClient.post('/scripts', data),
    update: (id, data) => apiClient.put(`/scripts/${id}`, data),
    delete: (id) => apiClient.delete(`/scripts/${id}`),
  },

  // AI Models APIs
  aiModels: {
    getAvailable: (params = {}) => apiClient.get('/models/available', { params }),
    getStatus: () => apiClient.get('/ai-models/status'),
  },

  // SIP APIs
  sip: {
    getProfiles: (params = {}) => apiClient.get('/sips/profiles', { params }),
    getAll: (params = {}) => apiClient.get('/sips', { params }),
    getById: (id) => apiClient.get(`/sips/${id}`),
    create: (data) => apiClient.post('/sips', data),
    update: (id, data) => apiClient.put(`/sips/${id}`, data),
    delete: (id) => apiClient.delete(`/sips/${id}`)
  },

  // System Settings APIs
  settings: {
    getSystemSettings: () => apiClient.get('/settings'),
    updateSystemSettings: (data) => apiClient.put('/settings', data),
    getTimezone: (timezone = 'Europe/Paris') => apiClient.get('/timezone/current', { 
      params: { timezone_name: timezone } 
    }),
  },

  // Voices APIs
  voices: {
    getAll: () => apiClient.get('/voices'),
    getByProvider: (ttsModelId) => apiClient.get('/voices/by-provider', { params: { tts_model_id: ttsModelId } }),
    test: (data) => apiClient.post('/voices/test', data),
  },

  // TTS APIs
  tts: {
    generate: (data) => apiClient.post('/tts/generate', data),
    clearCache: (campaignId) => apiClient.post('/tts/clear-cache', campaignId ? { campaignId } : {}),
  },

  // Conversations APIs
  conversations: {
    getAll: () => apiClient.get('/conversations'),
  },

  // Ambiances APIs
  ambiances: {
    getAvailable: () => apiClient.get('/ambiances/available'),
  },

  // Recordings APIs
  recordings: {
    getAll: (params = {}) => apiClient.get('/recordings', { params }),
    getByFilename: (filename) => apiClient.get(`/recordings/${filename}`, { responseType: 'blob' }),
  },

};

export default apiService;